import React, { useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { useTranslation } from 'react-i18next';
import authAPI from 'api/authApi';

import ModalLayout from '../ModalLayout/ModalLayout';

import c from './style.module.css';
import formStyles from '../ModalLayout/style.module.css';

const AuthModal = ({
    authMode,
    callback,
    closeCallback
}: {
    authMode?: 'login'|'register',
    callback?: (uid?: string, sid?: string) => void
    closeCallback?: () => void
}) => {

    const { t } = useTranslation();
    const [cookies, setCookie] = useCookies();

    const [email, setEmail] = useState<string>('');
    const [emailErr, setEmailErr] = useState<null | string>(null);
    const [code, setCode] = useState<string>('');
    const [codeErr, setCodeErr] = useState<null | string>(null);
    const [isCodeField, setIsCodeField] = React.useState(false);
    const [registerMode, setRegisterMode] = React.useState(authMode === 'register');
    const [btnLoading, setBtnLoading] = React.useState(false);

    const codeFormat = /^[0-9]{4}$/;

    const getAuthInfo = React.useCallback(async (modalComplete: () => void, data: { status: string, uid: string }) => {
        const { uid } = data;
        const sid = Math.random().toFixed(19).slice(2, 21);

        setCookie('uid', data.uid, { path: '/' });
        setCookie('sid', sid, { path: '/' });

        const resData = await authAPI
            .authMe(uid, sid)
            .then(res => res.data)
            .catch(console.error)

        if (resData.status === 'ok') {
            callback ? callback(uid, sid) : '';
        } else {
            console.error('not ok')
        }

        modalComplete();
    }, [setCookie]);

    const handleCode = React.useCallback(async (modalComplete: () => void, email: string, code: string) => {
        if (codeFormat.test(code)) {
            if (registerMode) {
                await authAPI
                    .sendCode(email, code)
                    .then((res) => {
                        switch (res.data.status) {
                            case 'ok':
                                getAuthInfo(modalComplete, res.data)
                                break
                            case 'vcode error':
                                modalComplete();
                                setCodeErr(t('incorrect code'))
                                break
                            default:
                                modalComplete();
                                setCodeErr(t('error'))
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        modalComplete();
                    });
            } else {
                await authAPI
                    .sendRestoreCode(email, code)
                    .then((res) => {
                        switch (res.data.status) {
                            case 'ok':
                                getAuthInfo(modalComplete, res.data);
                                break
                            case 'vcode error':
                                modalComplete();
                                setCodeErr(t('incorrect code'))
                                break
                            default:
                                modalComplete();
                                setCodeErr(t('error'))
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        modalComplete();
                    });
            }
        } else {
            setCodeErr(t('WRONG_CODE_FORMAT'));
            modalComplete();
        }
    }, [registerMode]);

    const handleEmail = React.useCallback(async (modalComplete: () => void, email: string) => {
        if (registerMode) {
            await authAPI.getCode(email)
                .then((res) => {
                    switch (res.data.status) {
                        case 'ok':
                            setIsCodeField(true);
                            break;
                        case 'email exist':
                            setEmailErr(t('user exists'))
                            break;
                        default:
                            setEmailErr(t('error'));
                    }
                })
                .catch(() => {
                    setEmailErr(t('error'));
                })
                .finally(() => {
                    modalComplete();
                });
        } else {
            await authAPI.restore(email)
                .then((res) => {
                    switch (res.data.status) {
                        case 'ok':
                            setIsCodeField(true);
                            break
                        case 'email not exist':
                            setEmailErr(t("user doesn't exists"));
                            break
                        default:
                            setEmailErr(t('error'));
                    }
                })
                .catch(() => {
                    setEmailErr(t('error'));
                })
                .finally(() => {
                    modalComplete();
                });
        }

    }, [registerMode]);

    const handleAuth = React.useCallback(async (modalComplete: () => void, email: string, code?: string) => {
        if (isCodeField) {
            if (email && code) {
                handleCode(modalComplete, email, code);
            }
        } else {
            if (email) {
                handleEmail(modalComplete, email);
            }
        }
    }, [isCodeField, registerMode]);

    const mainHandle = React.useCallback((modalComplete: () => void) => {
        if (email === '') {
            modalComplete();
            setEmailErr('Введите Email');
            return;
        }

        if (isCodeField && code === '') {
            modalComplete();
            setCodeErr(t('ENTER_CODE'));
            return;
        }

        handleAuth(modalComplete, email, code);
    }, [isCodeField, email, code]);

    return (
        <>
            <ModalLayout
                title={registerMode ? t('register') : t('log in')}
                closeModal={() => {
                    closeCallback
                        ? closeCallback()
                        : location.href = '/';
                }}
                bottomDescription={registerMode && (
                    <>
                        {t('register info')}{' '}
                        <a className="form__link" href="https://2anywhere.io/ld/ru/pp_agr/index.html">
                            {t('privacy policy')}
                        </a>
                        <span>, </span>
                        <a className="form__link" href="https://2anywhere.io/ld/ru/traveler_agr/index.html">
                            {t('agreement')}
                        </a>
                    </>
                )}
                description={registerMode ? (
                    <>
                        <div className={c.registerWrapper}>
                            {t('ALREADY_HAVE_ACCOUNT')}
                            <span className={c.registerLink} onClick={() => setRegisterMode(false)}>
                                {t('log in')}
                            </span>
                        </div>
                    </>
                )
                    :
                    (
                        <>
                            <div className={c.registerWrapper}>
                                {t('NO_ACCOUNT')}
                                <span className={c.registerLink} onClick={() => setRegisterMode(true)}>
                                    {t('register')}
                                </span>
                            </div>
                        </>
                    )}
                actions={[
                    {
                        title: isCodeField
                            ? (
                                registerMode ? t('register') : t('log in')
                            )
                            : t('GET_CODE'),
                        action: (modalComplete) => {
                            mainHandle(modalComplete);
                        },
                        loading: btnLoading
                    }
                ]}
            >
                <>
                    <div
                        className={formStyles.formWrapper}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                setBtnLoading(true);
                                mainHandle(() => {
                                    setBtnLoading(false);
                                });
                            }
                        }}
                    >
                        <div className={formStyles.inputWrapper}>
                            {emailErr && (
                                <p className={formStyles.formInputError}>
                                    {emailErr}
                                </p>
                            )}

                            <input
                                className={formStyles.formInput}
                                type="email"
                                name="email"
                                placeholder="example@mail.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailErr('');
                                }}
                            />
                        </div>

                        {isCodeField && (
                            <div className={formStyles.inputWrapper}>
                                {codeErr && (
                                    <p className={formStyles.formInputError}>
                                        {codeErr}
                                    </p>
                                )}

                                <input
                                    className={formStyles.formInput}
                                    type="text"
                                    name="code"
                                    placeholder={t('enter code')}
                                    value={code}
                                    onChange={(e) => {
                                        setCode(e.target.value);
                                        setCodeErr('');
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </>
            </ModalLayout>
        </>
    )
}

export default AuthModal;